import {
  PermissionCode,
  formatArticlePriceMinor,
  articleTaxClassSchema,
  articleTypeSchema,
  createArticlePurchasePriceRequestSchema,
  createArticleSupplierRequestSchema,
  createArticleUnitFactorRequestSchema,
  masterDataDateSchema,
  type ArticleDetail,
  type CreateArticlePurchasePriceRequest,
  type CreateArticleSupplierRequest,
  type CreateArticleUnitFactorRequest,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Resolver, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  useAddArticlePrice,
  useAddArticleSupplier,
  useAddArticleUnitFactor,
  useArticle,
  useArticleGroups,
  useArticlePrices,
  useArticleSupplierOptions,
  downloadArticleDocument,
  usePatchArticle,
  useRemoveArticleDocument,
  useUploadArticleDocument,
} from '@/platform/features/articles/use-articles';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useCostCenters,
  useCurrencies,
  useUnits,
} from '@/platform/features/master-data/use-master-data';

const TYPE_LABEL = { material: 'Material', service: 'Dienstleistung' } as const;
const TAX_LABEL = {
  standard: 'Standard',
  reduced: 'Ermäßigt',
  zero: 'Null',
  exempt: 'Befreit',
} as const;

const NONE = 'none';

const nameSuccessorSchema = z.object({
  name: z.string().trim().min(1).max(200),
  effectiveFrom: masterDataDateSchema,
});
type NameSuccessor = z.infer<typeof nameSuccessorSchema>;

const settingsSchema = z
  .object({
    type: articleTypeSchema,
    groupId: z.string().uuid(),
    baseUnitId: z.string().uuid(),
    purchaseUnitId: z.string().uuid(),
    packUnitId: z.string().uuid().nullable().optional(),
    stockManaged: z.boolean(),
    taxClass: articleTaxClassSchema,
    costCenterId: z.string().uuid().nullable().optional(),
    ean: z.string().trim().min(8).max(14).regex(/^\d+$/).nullable().optional().or(z.literal('')),
  })
  .superRefine((value, ctx) => {
    if (value.type === 'service' && value.stockManaged) {
      ctx.addIssue({
        code: 'custom',
        message: 'Eine Dienstleistung kann nicht lagergeführt sein.',
        path: ['stockManaged'],
      });
    }
  });
type SettingsValues = z.infer<typeof settingsSchema>;

function blankToNull(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function toastFailure(error: unknown, fallback: string) {
  toast.error(error instanceof ApiRequestError ? error.message : fallback);
}

function validityText(validFrom: string | null, validTo: string | null): string {
  const from = validFrom ? `ab ${validFrom}` : '';
  const to = validTo ? `bis ${validTo}` : 'offen';
  return [from, to].filter(Boolean).join(' · ');
}

function formatPriceMinor(priceMinor: number, currencyCode: string, minorUnits: number): string {
  return formatArticlePriceMinor(priceMinor, currencyCode, minorUnits);
}

export function ArticleDetailPage() {
  const { articleId = '' } = useParams();
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.ARTICLES_MANAGE);
  const canReadPrices = hasPermission(me.data, PermissionCode.ARTICLE_PRICES_READ);
  const canManagePrices = canWrite && hasPermission(me.data, PermissionCode.ARTICLE_PRICES_MANAGE);
  const canManageDocuments =
    canWrite && hasPermission(me.data, PermissionCode.ARTICLE_DOCUMENTS_MANAGE);
  const article = useArticle(articleId);
  const prices = useArticlePrices(articleId, canReadPrices);
  const patch = usePatchArticle(articleId);
  const row = article.data;
  const openName = row?.names.find((name) => name.validTo == null) ?? row?.names[0];
  const priceItems = canReadPrices ? (prices.data?.items ?? row?.prices ?? []) : [];

  async function setActive(isActive: boolean) {
    try {
      await patch.mutateAsync({ isActive });
      toast.success(isActive ? 'Artikel aktiviert.' : 'Artikel inaktiviert.');
    } catch (error) {
      toastFailure(
        error,
        isActive
          ? 'Der Artikel konnte nicht aktiviert werden.'
          : 'Der Artikel konnte nicht inaktiviert werden.',
      );
    }
  }

  return (
    <MasterDataListState
      pending={article.isPending}
      pendingLabel="Artikel wird geladen …"
      error={article.isError ? article.error : null}
      errorLabel="Der Artikel konnte nicht geladen werden."
      empty={!article.isPending && !article.isError && !row}
      emptyLabel="Der Artikel wurde nicht gefunden."
    >
      {row ? (
        <section className="space-y-8">
          <PlatformPageHeader
            title={openName?.name ?? row.articleNumber}
            description={`${row.articleNumber}${row.inactiveLabel ? ` · ${row.inactiveLabel}` : ''}`}
            action={
              canWrite ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={patch.isPending}
                  onClick={() => void setActive(!row.isActive)}
                >
                  {row.isActive ? 'Inaktivieren' : 'Aktivieren'}
                </Button>
              ) : undefined
            }
          />

          <section className="max-w-xl space-y-3">
            <h2 className="text-base font-semibold">Bezeichnung</h2>
            <ul className="space-y-2 text-sm">
              {row.names.map((name) => (
                <li key={name.id}>
                  {name.name}
                  {` · ${validityText(name.validFrom, name.validTo)}`}
                </li>
              ))}
            </ul>
            {canWrite ? <NameSuccessorForm articleId={articleId} /> : null}
          </section>

          <SettingsSection articleId={articleId} row={row} canWrite={canWrite} />

          <section className="space-y-3">
            <h2 className="text-base font-semibold">Lieferanten</h2>
            {row.suppliers.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch kein Lieferant.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {row.suppliers.map((supplier) => (
                  <li key={supplier.id}>
                    {supplier.partnerNumber ?? supplier.partnerId}
                    {` · ${supplier.supplierArticleNumber}`}
                    {supplier.isPreferred ? ' · bevorzugt' : ''}
                    {supplier.isActive ? '' : ' · inaktiv'}
                  </li>
                ))}
              </ul>
            )}
            {canWrite && row.isActive ? <SupplierForm articleId={articleId} /> : null}
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">Artikelfaktoren</h2>
            {row.unitFactors.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch kein artikelspezifischer Faktor.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {row.unitFactors.map((factor) => (
                  <li key={factor.id}>
                    {factor.numerator}/{factor.denominator}
                    {` · ${validityText(factor.validFrom, factor.validTo)}`}
                  </li>
                ))}
              </ul>
            )}
            {canWrite && row.isActive ? (
              <UnitFactorForm articleId={articleId} baseUnitId={row.baseUnitId} />
            ) : null}
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">Dokumente</h2>
            <DocumentList
              articleId={articleId}
              documents={row.documents}
              canRemove={canManageDocuments}
            />
            {canManageDocuments && row.isActive ? <DocumentForm articleId={articleId} /> : null}
          </section>

          {canReadPrices ? (
            <section className="space-y-3">
              <h2 className="text-base font-semibold">Einkaufspreise</h2>
              {prices.isPending ? (
                <p className="text-sm text-muted-foreground">Preise werden geladen …</p>
              ) : prices.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  Die Preise konnten nicht geladen werden.
                </p>
              ) : priceItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch kein Einkaufspreis.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {priceItems.map((price) => (
                    <li key={price.id}>
                      {formatPriceMinor(price.priceMinor, price.currencyCode, price.minorUnits)}
                      {` · ab ${price.minQuantity}`}
                      {price.discountBps != null ? ` · ${price.discountBps} Bp` : ''}
                      {` · ${validityText(price.validFrom, price.validTo)}`}
                    </li>
                  ))}
                </ul>
              )}
              {canManagePrices && row.isActive ? (
                <PriceForm articleId={articleId} suppliers={row.suppliers} />
              ) : null}
            </section>
          ) : null}
        </section>
      ) : null}
    </MasterDataListState>
  );
}

function NameSuccessorForm({ articleId }: { articleId: string }) {
  const copy = useVoiceCopy();
  const patch = usePatchArticle(articleId);
  const form = useForm<NameSuccessor>({
    resolver: zodResolver(nameSuccessorSchema),
    defaultValues: { name: '', effectiveFrom: '' },
  });

  async function onValid(values: NameSuccessor) {
    try {
      await patch.mutateAsync(values);
      toast.success('Bezeichnung fortgeschrieben.');
      form.reset({ name: '', effectiveFrom: '' });
    } catch (error) {
      toastFailure(error, 'Die Bezeichnung konnte nicht fortgeschrieben werden.');
    }
  }

  return (
    <form className="space-y-3" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="successor-name">Neue Bezeichnung</Label>
        <Input id="successor-name" {...form.register('name')} />
        {form.formState.errors.name ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.designation')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="successor-from">Gültig ab</Label>
        <Input id="successor-from" placeholder="JJJJ-MM-TT" {...form.register('effectiveFrom')} />
        {form.formState.errors.effectiveFrom ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.date')}
          </p>
        ) : null}
      </div>
      <Button type="submit" disabled={patch.isPending}>
        Speichern
      </Button>
    </form>
  );
}

function SettingsSection({
  articleId,
  row,
  canWrite,
}: {
  articleId: string;
  row: ArticleDetail;
  canWrite: boolean;
}) {
  const groups = useArticleGroups(false);
  const units = useUnits();
  const costCenters = useCostCenters();
  const patch = usePatchArticle(articleId);
  const form = useForm<SettingsValues>({
    resolver: zodResolver(settingsSchema),
    values: {
      type: row.type,
      groupId: row.groupId,
      baseUnitId: row.baseUnitId,
      purchaseUnitId: row.purchaseUnitId,
      packUnitId: row.packUnitId,
      stockManaged: row.stockManaged,
      taxClass: row.taxClass,
      costCenterId: row.costCenterId,
      ean: row.ean ?? '',
    },
  });
  const type = form.watch('type');

  async function onValid(values: SettingsValues) {
    try {
      await patch.mutateAsync({
        ...values,
        costCenterId: values.costCenterId ?? null,
        packUnitId: values.packUnitId ?? null,
        ean: values.ean === '' || values.ean == null ? null : values.ean,
      });
      toast.success('Artikel gespeichert.');
    } catch (error) {
      toastFailure(error, 'Der Artikel konnte nicht gespeichert werden.');
    }
  }

  const unitLabel = (id: string) => units.data?.items.find((unit) => unit.id === id)?.code ?? id;
  const groupLabel =
    groups.data?.items.find((group) => group.id === row.groupId)?.name ?? row.groupId;

  if (!canWrite) {
    return (
      <section className="space-y-2 text-sm">
        <h2 className="text-base font-semibold">Stammdaten</h2>
        <p>
          {TYPE_LABEL[row.type]} · Gruppe {groupLabel} · Basis {unitLabel(row.baseUnitId)} · Einkauf{' '}
          {unitLabel(row.purchaseUnitId)} · {TAX_LABEL[row.taxClass]}
          {row.stockManaged ? ' · lagergeführt' : ''}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-xl space-y-3">
      <h2 className="text-base font-semibold">Stammdaten</h2>
      <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
        <div className="space-y-2">
          <Label htmlFor="settings-type">Typ</Label>
          <Select
            value={form.watch('type')}
            onValueChange={(value) => {
              void form.setValue('type', value as 'material' | 'service');
              if (value === 'service') {
                form.setValue('stockManaged', false);
              }
            }}
          >
            <SelectTrigger id="settings-type" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="material">Material</SelectItem>
              <SelectItem value="service">Dienstleistung</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-group">Gruppe</Label>
          <Select
            value={form.watch('groupId')}
            onValueChange={(value) => form.setValue('groupId', value)}
          >
            <SelectTrigger id="settings-group" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {groups.data?.items.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.code} · {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-base">Basiseinheit</Label>
          <Select
            value={form.watch('baseUnitId')}
            onValueChange={(value) => form.setValue('baseUnitId', value)}
          >
            <SelectTrigger id="settings-base" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {units.data?.items.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.code} · {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-purchase">Einkaufseinheit</Label>
          <Select
            value={form.watch('purchaseUnitId')}
            onValueChange={(value) => form.setValue('purchaseUnitId', value)}
          >
            <SelectTrigger id="settings-purchase" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {units.data?.items.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.code} · {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-tax">Steuerklasse</Label>
          <Select
            value={form.watch('taxClass')}
            onValueChange={(value) => form.setValue('taxClass', value as ArticleDetail['taxClass'])}
          >
            <SelectTrigger id="settings-tax" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="reduced">Ermäßigt</SelectItem>
              <SelectItem value="zero">Null</SelectItem>
              <SelectItem value="exempt">Befreit</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-cost">Kostenstelle</Label>
          <Select
            value={form.watch('costCenterId') ?? NONE}
            onValueChange={(value) =>
              form.setValue('costCenterId', blankToNull(value === NONE ? '' : value))
            }
          >
            <SelectTrigger id="settings-cost" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Keine</SelectItem>
              {costCenters.data?.items.map((center) => (
                <SelectItem key={center.id} value={center.id}>
                  {center.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-pack">Verpackungseinheit</Label>
          <Select
            value={form.watch('packUnitId') ?? NONE}
            onValueChange={(value) =>
              form.setValue('packUnitId', blankToNull(value === NONE ? '' : value))
            }
          >
            <SelectTrigger id="settings-pack" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Keine</SelectItem>
              {units.data?.items.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.code} · {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-ean">EAN</Label>
          <Input id="settings-ean" {...form.register('ean')} />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="settings-stock"
            type="checkbox"
            className="size-4"
            disabled={type === 'service'}
            checked={type === 'service' ? false : Boolean(form.watch('stockManaged'))}
            onChange={(event) => {
              form.setValue('stockManaged', type === 'service' ? false : event.target.checked);
            }}
          />
          <Label htmlFor="settings-stock">Für ein späteres Lager vorgesehen</Label>
        </div>
        {form.formState.errors.stockManaged ? (
          <p role="alert" className="text-sm text-destructive">
            Eine Dienstleistung kann nicht lagergeführt sein.
          </p>
        ) : null}
        <Button type="submit" disabled={patch.isPending}>
          Speichern
        </Button>
      </form>
    </section>
  );
}

function SupplierForm({ articleId }: { articleId: string }) {
  const copy = useVoiceCopy();
  const partners = useArticleSupplierOptions(true);
  const add = useAddArticleSupplier(articleId);
  const form = useForm<CreateArticleSupplierRequest>({
    resolver: zodResolver(createArticleSupplierRequestSchema),
    defaultValues: {
      partnerId: '',
      supplierArticleNumber: '',
      isActive: true,
      isPreferred: false,
    },
  });

  async function onValid(values: CreateArticleSupplierRequest) {
    try {
      await add.mutateAsync(values);
      toast.success('Lieferant zugeordnet.');
      form.reset({ partnerId: '', supplierArticleNumber: '', isActive: true, isPreferred: false });
    } catch (error) {
      toastFailure(error, 'Der Lieferant konnte nicht zugeordnet werden.');
    }
  }

  return (
    <form className="max-w-xl space-y-3" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="supplier-partner">Geschäftspartner</Label>
        <Select
          value={form.watch('partnerId') || NONE}
          onValueChange={(value) => form.setValue('partnerId', value === NONE ? '' : value)}
        >
          <SelectTrigger id="supplier-partner" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Bitte wählen</SelectItem>
            {partners.data?.items.map((partner) => (
              <SelectItem key={partner.id} value={partner.id}>
                {partner.partnerNumber}
                {partner.legalName ? ` · ${partner.legalName}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.formState.errors.partnerId ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.partner')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="supplier-number">Lieferantenartikelnummer</Label>
        <Input id="supplier-number" {...form.register('supplierArticleNumber')} />
        {form.formState.errors.supplierArticleNumber ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.supplier-sku')}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <input
          id="supplier-preferred"
          type="checkbox"
          className="size-4"
          {...form.register('isPreferred')}
        />
        <Label htmlFor="supplier-preferred">Bevorzugter Lieferant</Label>
      </div>
      <Button type="submit" disabled={add.isPending}>
        Speichern
      </Button>
    </form>
  );
}

function PriceForm({
  articleId,
  suppliers,
}: {
  articleId: string;
  suppliers: ArticleDetail['suppliers'];
}) {
  const copy = useVoiceCopy();
  const currencies = useCurrencies();
  const units = useUnits();
  const add = useAddArticlePrice(articleId);
  const form = useForm<CreateArticlePurchasePriceRequest>({
    resolver: zodResolver(
      createArticlePurchasePriceRequestSchema,
    ) as Resolver<CreateArticlePurchasePriceRequest>,
    defaultValues: {
      articleSupplierId: '',
      currencyId: '',
      unitId: '',
      priceMinor: undefined as unknown as number,
      minQuantity: 1,
      discountBps: null,
      validFrom: '',
      validTo: null,
    },
  });

  async function onValid(values: CreateArticlePurchasePriceRequest) {
    try {
      await add.mutateAsync({
        ...values,
        discountBps: values.discountBps ?? null,
        validTo: values.validTo ?? null,
      });
      toast.success('Einkaufspreis angelegt.');
      form.reset({
        articleSupplierId: '',
        currencyId: '',
        unitId: '',
        priceMinor: undefined as unknown as number,
        minQuantity: 1,
        discountBps: null,
        validFrom: '',
        validTo: null,
      });
    } catch (error) {
      toastFailure(error, 'Der Einkaufspreis konnte nicht angelegt werden.');
    }
  }

  return (
    <form className="max-w-xl space-y-3" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="price-supplier">Lieferant</Label>
        <Select
          value={form.watch('articleSupplierId') || NONE}
          onValueChange={(value) => form.setValue('articleSupplierId', value === NONE ? '' : value)}
        >
          <SelectTrigger id="price-supplier" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Bitte wählen</SelectItem>
            {suppliers
              .filter((supplier) => supplier.isActive)
              .map((supplier) => (
                <SelectItem key={supplier.id} value={supplier.id}>
                  {supplier.partnerNumber ?? supplier.partnerId} · {supplier.supplierArticleNumber}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {form.formState.errors.articleSupplierId ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.supplier')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="price-currency">Währung</Label>
        <Select
          value={form.watch('currencyId') || NONE}
          onValueChange={(value) => form.setValue('currencyId', value === NONE ? '' : value)}
        >
          <SelectTrigger id="price-currency" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Bitte wählen</SelectItem>
            {currencies.data?.items
              .filter((currency) => currency.isActive)
              .map((currency) => (
                <SelectItem key={currency.id} value={currency.id}>
                  {currency.code}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {form.formState.errors.currencyId ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.currency')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="price-unit">Einheit</Label>
        <Select
          value={form.watch('unitId') || NONE}
          onValueChange={(value) => form.setValue('unitId', value === NONE ? '' : value)}
        >
          <SelectTrigger id="price-unit" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Bitte wählen</SelectItem>
            {units.data?.items
              .filter((unit) => unit.isActive)
              .map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.code} · {unit.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {form.formState.errors.unitId ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.unit')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="price-minor">Preis in Cent</Label>
        <Input
          id="price-minor"
          type="number"
          inputMode="numeric"
          {...form.register('priceMinor', { valueAsNumber: true })}
        />
        {form.formState.errors.priceMinor ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.price-cent')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="price-min-qty">Mindestmenge</Label>
        <Input
          id="price-min-qty"
          type="number"
          inputMode="numeric"
          {...form.register('minQuantity', { valueAsNumber: true })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="price-discount">Rabatt in Basispunkten</Label>
        <Input
          id="price-discount"
          type="number"
          inputMode="numeric"
          {...form.register('discountBps', {
            setValueAs: (value: unknown) => {
              if (value == null || value === '') {
                return null;
              }
              const n = Number(value);
              return Number.isFinite(n) ? n : null;
            },
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="price-from">Gültig ab</Label>
        <Input id="price-from" placeholder="JJJJ-MM-TT" {...form.register('validFrom')} />
        {form.formState.errors.validFrom ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.date')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="price-to">Gültig bis</Label>
        <Input
          id="price-to"
          placeholder="JJJJ-MM-TT"
          {...form.register('validTo', { setValueAs: blankToNull })}
        />
      </div>
      <Button type="submit" disabled={add.isPending}>
        Speichern
      </Button>
    </form>
  );
}

function UnitFactorForm({ articleId, baseUnitId }: { articleId: string; baseUnitId: string }) {
  const units = useUnits();
  const add = useAddArticleUnitFactor(articleId);
  const form = useForm<CreateArticleUnitFactorRequest>({
    resolver: zodResolver(createArticleUnitFactorRequestSchema),
    defaultValues: {
      fromUnitId: '',
      toUnitId: baseUnitId,
      numerator: 1,
      denominator: 1,
      validFrom: '',
      validTo: null,
    },
  });

  async function onValid(values: CreateArticleUnitFactorRequest) {
    try {
      await add.mutateAsync({ ...values, toUnitId: baseUnitId, validTo: values.validTo ?? null });
      toast.success('Artikelfaktor angelegt.');
      form.reset({
        fromUnitId: '',
        toUnitId: baseUnitId,
        numerator: 1,
        denominator: 1,
        validFrom: '',
        validTo: null,
      });
    } catch (error) {
      toastFailure(error, 'Der Artikelfaktor konnte nicht angelegt werden.');
    }
  }

  return (
    <form className="max-w-xl space-y-3" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="factor-from">Ausgangseinheit</Label>
        <Select
          value={form.watch('fromUnitId') || NONE}
          onValueChange={(value) => form.setValue('fromUnitId', value === NONE ? '' : value)}
        >
          <SelectTrigger id="factor-from" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Bitte wählen</SelectItem>
            {units.data?.items
              .filter((unit) => unit.isActive && unit.id !== baseUnitId)
              .map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.code} · {unit.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="factor-num">Zähler</Label>
        <Input
          id="factor-num"
          type="number"
          {...form.register('numerator', { valueAsNumber: true })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="factor-den">Nenner</Label>
        <Input
          id="factor-den"
          type="number"
          {...form.register('denominator', { valueAsNumber: true })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="factor-from-date">Gültig ab</Label>
        <Input id="factor-from-date" placeholder="JJJJ-MM-TT" {...form.register('validFrom')} />
      </div>
      <Button type="submit" disabled={add.isPending}>
        Speichern
      </Button>
    </form>
  );
}

function DocumentList({
  articleId,
  documents,
  canRemove,
}: {
  articleId: string;
  documents: ArticleDetail['documents'];
  canRemove: boolean;
}) {
  const remove = useRemoveArticleDocument(articleId);

  async function openFile(documentId: string, fileName: string) {
    try {
      await downloadArticleDocument(articleId, documentId, fileName);
    } catch (error) {
      toastFailure(error, 'Das Dokument konnte nicht geöffnet werden.');
    }
  }

  async function removeFile(documentId: string) {
    try {
      await remove.mutateAsync(documentId);
      toast.success('Dokument entfernt.');
    } catch (error) {
      toastFailure(error, 'Das Dokument konnte nicht entfernt werden.');
    }
  }

  if (documents.length === 0) {
    return <p className="text-sm text-muted-foreground">Noch kein Dokument.</p>;
  }

  return (
    <ul className="space-y-2 text-sm">
      {documents.map((doc) => {
        const canOpen = doc.scanStatus === 'CLEAN' || doc.scanStatus === 'SKIPPED';
        return (
          <li key={doc.id} className="flex flex-wrap items-center gap-2">
            <span>
              {doc.fileName} · {doc.kind} · {doc.scanStatus}
            </span>
            {canOpen ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void openFile(doc.id, doc.fileName)}
              >
                Öffnen
              </Button>
            ) : null}
            {canRemove ? (
              <Button
                type="button"
                variant="outline"
                disabled={remove.isPending}
                onClick={() => void removeFile(doc.id)}
              >
                Entfernen
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function DocumentForm({ articleId }: { articleId: string }) {
  const copy = useVoiceCopy();
  const upload = useUploadArticleDocument(articleId);
  const [kind, setKind] = useState('document');

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const file = data.get('file');
    if (!(file instanceof File) || file.size === 0) {
      toast.error(copy('platform.form.file'));
      return;
    }
    try {
      await upload.mutateAsync({ kind, file });
      toast.success('Dokument hochgeladen.');
      form.reset();
      setKind('document');
    } catch (error) {
      toastFailure(error, 'Das Dokument konnte nicht hochgeladen werden.');
    }
  }

  return (
    <form className="max-w-xl space-y-3" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="doc-kind">Art</Label>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger id="doc-kind" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="document">Dokument</SelectItem>
            <SelectItem value="image">Bild</SelectItem>
            <SelectItem value="datasheet">Datenblatt</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="doc-file">Datei</Label>
        <Input id="doc-file" name="file" type="file" />
      </div>
      <Button type="submit" disabled={upload.isPending}>
        Hochladen
      </Button>
    </form>
  );
}
